import React, { useEffect, useState } from 'react'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import { Box, Button, ButtonBase, IconButton, Typography } from '@mui/material'
import moment, { type Moment } from 'moment'
import { muahangKiotLike } from '../../../layout/muahangKiotTokens'
import { DATE_ISO, WEEKDAYS_VI, parseIsoDate } from './dateTimeUtils'

export type DateCalendarProps = {
  value: string
  onPick: (isoDate: string) => void
  onClose: () => void
  /** Chọn ngày xong có đóng panel không. Mặc định true. */
  closeOnPick?: boolean
  /** Hiện nút Hôm nay / Xong. Mặc định true. */
  showActions?: boolean
}

/** Lịch chọn 1 ngày — base dùng trong DateTimeInput / Popover. */
export function DateCalendar({
  value,
  onPick,
  onClose,
  closeOnPick = true,
  showActions = true,
}: DateCalendarProps) {
  const selected = parseIsoDate(value)
  const [viewMonth, setViewMonth] = useState(() =>
    (selected ?? moment()).clone().startOf('month'),
  )

  useEffect(() => {
    if (selected) setViewMonth(selected.clone().startOf('month'))
  }, [value]) // eslint-disable-line react-hooks/exhaustive-deps

  const start = viewMonth.clone().startOf('month')
  const end = viewMonth.clone().endOf('month')
  const gridStart = start.clone().startOf('isoWeek')
  const gridEnd = end.clone().endOf('isoWeek')
  const days: Moment[] = []
  for (let d = gridStart.clone(); d.isSameOrBefore(gridEnd, 'day'); d.add(1, 'day')) {
    days.push(d.clone())
  }
  const today = moment().startOf('day')

  return (
    <Box sx={{ width: 292, p: 1.25, boxSizing: 'border-box' }}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          mb: 1,
          px: 0.25,
        }}
      >
        <IconButton
          size="small"
          onClick={() => setViewMonth((m) => m.clone().subtract(1, 'month'))}
          aria-label="Tháng trước"
          sx={{ p: 0.5 }}
        >
          <ChevronLeftIcon sx={{ fontSize: 20 }} />
        </IconButton>
        <Typography
          sx={{
            fontWeight: 700,
            fontSize: 14,
            fontFamily: muahangKiotLike.kiotFontStack,
            color: muahangKiotLike.kiotTextPrimary,
          }}
        >
          Tháng {viewMonth.format('M/YYYY')}
        </Typography>
        <IconButton
          size="small"
          onClick={() => setViewMonth((m) => m.clone().add(1, 'month'))}
          aria-label="Tháng sau"
          sx={{ p: 0.5 }}
        >
          <ChevronRightIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          mb: 0.5,
        }}
      >
        {WEEKDAYS_VI.map((w) => (
          <Typography
            key={w}
            sx={{
              textAlign: 'center',
              fontSize: 11,
              fontWeight: 700,
              color: muahangKiotLike.kiotTextMuted,
              lineHeight: '24px',
              fontFamily: muahangKiotLike.kiotFontStack,
            }}
          >
            {w}
          </Typography>
        ))}
      </Box>

      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(7, 1fr)',
          gap: '2px',
          mb: 1.25,
        }}
      >
        {days.map((day) => {
          const inMonth = day.month() === viewMonth.month()
          const isSelected = Boolean(selected && day.isSame(selected, 'day'))
          const isToday = day.isSame(today, 'day')
          return (
            <ButtonBase
              key={day.format(DATE_ISO)}
              onClick={() => {
                onPick(day.format(DATE_ISO))
                if (closeOnPick) onClose()
              }}
              sx={{
                height: 36,
                borderRadius: '8px',
                fontSize: 13,
                fontWeight: isSelected || isToday ? 700 : 500,
                fontVariantNumeric: 'tabular-nums',
                fontFamily: muahangKiotLike.kiotFontStack,
                color: isSelected
                  ? '#fff'
                  : inMonth
                    ? muahangKiotLike.kiotTextPrimary
                    : muahangKiotLike.kiotTextMuted,
                bgcolor: isSelected
                  ? 'primary.main'
                  : isToday
                    ? 'rgba(25, 118, 210, 0.1)'
                    : 'transparent',
                outline: isToday && !isSelected ? '1px solid' : 'none',
                outlineColor: 'primary.main',
                outlineOffset: -1,
                '&:hover': {
                  bgcolor: isSelected ? 'primary.dark' : 'rgba(15, 23, 42, 0.06)',
                },
              }}
            >
              {day.date()}
            </ButtonBase>
          )
        })}
      </Box>

      {showActions ? (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            fullWidth
            size="small"
            variant="outlined"
            onClick={() => {
              onPick(today.format(DATE_ISO))
              if (closeOnPick) onClose()
            }}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: '8px',
              fontFamily: muahangKiotLike.kiotFontStack,
            }}
          >
            Hôm nay
          </Button>
          <Button
            fullWidth
            size="small"
            variant="contained"
            onClick={onClose}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: '8px',
              fontFamily: muahangKiotLike.kiotFontStack,
              boxShadow: 'none',
            }}
          >
            Xong
          </Button>
        </Box>
      ) : null}
    </Box>
  )
}

export default DateCalendar
